import LoginPage from "./pages/Login"
import { auth, db } from "./firebase";
import { signInWithCustomToken } from "firebase/auth";
import { ref, get, remove } from "firebase/database";
import { useEffect, useRef, useState } from "react";
import ChatPage from "./pages/Chat";
import ChatDisplayPage from "./pages/ChatDisplay";
import { BrowserRouter as Router, Routes, Route, } from 'react-router-dom';
import { analytics } from "./firebase";
import { setUserId, setUserProperties } from "firebase/analytics";
import { isUserProfileEffectivelyEnabled, mergeSocSettingsFromMessage, SocSettings, SOC_SETTINGS_DEFAULTS } from "./socSettings";

declare global {
  interface Window {
    dataLayer?: Array<Record<string, any>>;
  }
}

export function App() {
  const [userAuth, setUserAuth] = useState<boolean>(false);
  const [socSettings, setSocSettings] = useState<SocSettings>(SOC_SETTINGS_DEFAULTS);
  const socSettingsRef = useRef<SocSettings>(SOC_SETTINGS_DEFAULTS);

  useEffect(() => {
    const allowedOrigins = (process.env.REACT_APP_ALLOWED_PARENT_ORIGINS || '')
      .split(',')
      .map((origin) => origin.trim())
      .filter(Boolean);

    function receiveMessage(event: MessageEvent) {
      if (allowedOrigins.length > 0 && !allowedOrigins.includes(event.origin)) {
        return;
      }
      if (typeof event.data !== 'object' || event.data === null) {
        return;
      }

      const data = event.data as Record<string, unknown>;
      const messageSettings = mergeSocSettingsFromMessage(data, socSettingsRef.current);
      socSettingsRef.current = messageSettings;
      setSocSettings(messageSettings);

      const chatSocToken = typeof data.chatSocToken === 'string' ? data.chatSocToken : '';
      const userId = typeof data.userId === 'string' ? data.userId : '';
      const corpId = typeof data.corpId === 'string' ? data.corpId : '';

      if (chatSocToken) {
        signInWithCustomToken(auth, chatSocToken)
          .then(() => {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'user_info',
              userId,
              corpId,
            })
            setUserAuth(true);
            setUserId(analytics, userId);
            setUserProperties(analytics, {
              corpId
            });

            if (userId && !messageSettings.companyPermitUserProfiles) {
              const profileRef = ref(db, 'profiles/' + userId);
              get(profileRef).then((snapshot) => {
                if (snapshot.exists()) {
                  remove(profileRef).then(() => {
                    console.log('[ChatSOC] User profile deleted — company disabled User Profiles.');
                  }).catch((err) => {
                    console.error('[ChatSOC] Failed to delete user profile:', err);
                  });
                }
              }).catch((err) => {
                console.error('[ChatSOC] Failed to check user profile:', err);
              });
            }
          })
          .catch(() => {
            // Token may be expired/stale if parent app re-sends it — safe to ignore
          })
      }
    }
    window.addEventListener('message', receiveMessage, false);
    return () => {
      window.removeEventListener('message', receiveMessage, false);
    };
  }, []);

  return (
    <Router>
      <Routes>
        <Route path="/" element={<LoginPage userAuth={userAuth} />} />
        <Route
          path="/chat"
          element={userAuth ? (
            <ChatPage
              useAutocomplete={socSettings.userAutocomplete}
              canLinkCaseCitations={socSettings.userPrecedentCasesPreview || socSettings.userPrecedentCasesPaid}
              useUserProfile={isUserProfileEffectivelyEnabled(socSettings)}
              companyChatRetentionDays={socSettings.companyChatRetentionDays}
            />
          ) : <LoginPage userAuth={userAuth} />}
        />
        <Route path="/chatHistory" element={<ChatDisplayPage />} />
      </Routes>
    </Router>
  )
}
